import { useCallback, useEffect, useRef } from 'react'
import { useAnalytics } from '@keiko-app/react-google-analytics'
import { useUserStore } from '@/store/useUserStore'
import { getPlatform } from '@/utils/platformDetection'
import { generateRandomId } from '@/utils/generateRandomId'
import { AnalyticsEvents } from './events'
import { AnalyticsEventProps, StandardProperties } from './types'

// Session ID key for sessionStorage
const SESSION_ID_KEY = 'kem_analytics_session_id'

export const useAppAnalytics = () => {
  const { tracker } = useAnalytics()
  const userAuthenticated = useUserStore((state) => state.userAuthenticated)
  
  // Persistent session ID for the browser tab session
  const sessionId = useRef<string>('')

  useEffect(() => {
    let sid = sessionStorage.getItem(SESSION_ID_KEY)
    if (!sid) {
      sid = generateRandomId()
      sessionStorage.setItem(SESSION_ID_KEY, sid)
    }
    sessionId.current = sid
  }, [])

  const getStandardProperties = useCallback((): StandardProperties => {
    const platform = getPlatform()
    
    // Map KYC status
    let kycStatus: StandardProperties['kyc_status'] = 'unverified'
    if (userAuthenticated?.hasIdentityVerified) {
      kycStatus = 'verified'
    } else if (userAuthenticated) {
      // Logic for 'in_review' could be added here if available in store
      // For now, we fallback to unverified vs verified based on boolean
      kycStatus = 'unverified' 
    }

    return {
      user_id: userAuthenticated?.user_id,
      device_type: platform,
      kyc_status: kycStatus,
      session_id: sessionId.current,
    }
  }, [userAuthenticated])

  const trackEvent = useCallback(
    <K extends keyof AnalyticsEventProps>(
      eventName: K,
      props: AnalyticsEventProps[K]
    ) => {
      const standardProps = getStandardProperties()
      const mergedProps = {
        ...standardProps,
        ...props,
      }
      
      try {
        tracker.trackEvent(eventName, mergedProps)
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[Analytics] Failed to track event:', error)
        }
      }
      
      // Debug logging
      if (import.meta.env.DEV) {
        console.group(`[Analytics] ${eventName}`)
        console.log('Props:', mergedProps)
        console.groupEnd()
      }
    },
    [tracker, getStandardProperties]
  )

  return {
    trackEvent,
    events: AnalyticsEvents,
  }
}
